import { useCallback, useEffect, useMemo, useState } from "react";
import { useCreateContractMutation, useGetContractQuery, useGetContractsQuery, useGetContractTemplatesQuery, useGetLocationQuery, useUpdateContractMutation } from "../../../api/plamo-api";
import { useSelector } from "react-redux";
import { Alert, Button, Col, Form, Row, Space, Tabs, theme } from "antd";
import { useNavigate } from "react-router-dom";
import { useContractSetting } from "../pages/settings/contract-settings-page";
import { showSuccessMessage } from "../util/with-save-notification";
import dayjs from "dayjs";
import FormItem from "../form/form-item";
import Editor from "../util/editor";

const signaturesTemplate = `
    <!-- <div style="height: 1cm">&nbsp;</div> -->
    <table>
		<tr><td style="width: 5cm; height: 1cm; border-bottom: 1px solid black;">{CONTRACT_LOCATION}, {DATE}</td><td style="width: 2cm"></td><td style="width: 7cm; border-bottom: 1px solid black;">
    <img src="{CONTRACT_SIGNATURE}" style="max-height: 1cm; max-width: 7cm; object-fit: contain;" />
    </td></tr>
		<tr><td style="width: 5cm">Ort, Datum</td><td style="width: 2cm"></td><td style="width: 7cm;">{CONTRACT_SIGNER_LINE_1}</td></tr>
		<tr><td style="width: 5cm"></td><td style="width: 2cm"></td><td style="width: 7cm;">{CONTRACT_SIGNER_LINE_2}</td></tr>
		<tr><td style="width: 5cm; height: 1cm; border-bottom: 1px solid black;"></td><td style="width: 2cm"></td><td style="width: 7cm; border-bottom: 1px solid black;"></td></tr>
		<tr><td style="width: 5cm">Ort, Datum</td><td style="width: 2cm"></td><td style="width: 7cm;">{FIRST_NAME} {LAST_NAME}</td></tr>
		<tr><td style="width: 5cm"></td><td style="width: 2cm"></td><td style="width: 7cm;">{ROLE}</td></tr>
	  </table>
    <!-- <div style="height:1px"></div> -->
`

const signatureCustomerTemplate = `
    <table>
		<tr><td style="width: 5cm; height: 1cm; border-bottom: 1px solid black;"></td></tr>
		<tr><td style="width: 5cm">Unterschrift des/der Leistungsnehmers/in</td></tr>
	  </table>
    <div style="height:1px"></div>
`

function ContractDetails({ contractId, fillContract, contractObjectId, contractObjectType, signerFirstName, signerLastName, signerRole, navigationBasePath, locationId, address, showValidUntil = false }) {
  const { data: contract } = useGetContractQuery({ id: contractId }, { skip: contractId === 'new' }) as { data: any }
  const [form] = Form.useForm();
  const effectiveDate = Form.useWatch("effectiveDate", form);
  const validUntil = Form.useWatch("validUntil", form);
  const [creatingContract, setCreatingContract] = useState<boolean>(false);
  const { data } = useGetContractTemplatesQuery({ f: JSON.stringify({ contractType: { c: 'EQ', v: contractObjectType }}), st: JSON.stringify({ title: "asc" })}) as any;
  const { data: location } = useGetLocationQuery({ id: locationId }, { skip: !locationId }) as any;
  const [createContract] = useCreateContractMutation();
  const [updateContract] = useUpdateContractMutation();
  const userToken = useSelector(state => (state as any).user?.userToken);
  const contractTemplateId = Form.useWatch('contractTemplateId', form);
  const navigate = useNavigate();
  const [missingFields, setMissingFields] = useState<any[]>([]);
  const [contractSetting] = useContractSetting();
  const { data: existingContracts } = useGetContractsQuery({ f: JSON.stringify({ objectType: { c: "EQ", v: contractObjectType }, objectId: { c: "EQ", v: contractObjectId } }), st: JSON.stringify({ id: "desc" }) }) as any;

  const lastContractDate = useMemo(() => (existingContracts?.items || []).filter(c => c.id !== contractId)[0]?.date, [existingContracts, contractId]);
      
  const contractTemplate = useMemo(() => data?.items.find(i => i.id === contractTemplateId), [contractTemplateId, data?.items])

  useEffect(() => {
    form.setFieldsValue({...contract, contractTemplateId: contract?.contractTemplate});
  }, [form, contract])

  useEffect(() => {
    if(contractTemplate) {
      const { filledContract, filledLetter, missingFields } = fillContract(contractTemplate, lastContractDate, effectiveDate, validUntil);
      form.setFieldsValue({
        contractTemplateId,
        title: contractTemplate?.title,
        content: filledContract,
        letterContent: filledLetter
      })
      
      setMissingFields(missingFields);
    }
  }, [contractTemplate, form, fillContract, setMissingFields, contractTemplateId, lastContractDate, effectiveDate, validUntil])
  
  const { token } = theme.useToken();

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    padding: 24,
  };

  const onFinish = useCallback(async (values: any) => {
    setCreatingContract(true);

    const signatures = signaturesTemplate
      .replace(/{CONTRACT_LOCATION}/, contractSetting.contractLocation)
      .replace(/{CONTRACT_SIGNER_LINE_1}/, contractSetting.contractSignerLine1 || "Unterschrift")
      .replace(/{CONTRACT_SIGNER_LINE_2}/, contractSetting.contractSignerLine2 || "Unterschrift")
      .replace(/{CONTRACT_SIGNATURE}/, contractSetting.contractSignature || "")
      .replace(/{DATE}/, dayjs().format('DD.MM.YYYY'))
      .replace(/{FIRST_NAME}/, signerFirstName)
      .replace(/{LAST_NAME}/, signerLastName)
      .replace(/{ROLE}/, signerRole || "Kunde/Arbeitnehmer")

    const contentExtended = `<span class="title">Vertrag ${signerFirstName} ${signerLastName}</span>` + 
      `<p class="footerLeft">${location?.footerLeft?.replace(/\n/g, '<br/>')}</p>` +
      `<p class="footerCenter">${location?.footerCenter?.replace(/\n/g, '<br/>')}</p>` +
      `<p class="footerRight">${location?.footerRight?.replace(/\n/g, '<br/>')}</p>` +
      (values.content || fillContract(contractTemplate, lastContractDate, effectiveDate, validUntil).filledContract)
        .replace(/<p class="ql-align-((justify)|(center))"><br\/?><\/p>/g, '').replace(/<p class="ql-indent-1"><br\/?><\/p>/g, '').replace(/<p><br\/?><\/p>/g, '')
        .replace(/<h2.*?>/g, '<h2 data-break-after="avoid">')
        //.replace(/<p>{SEITENUMBRUCH}<\/p>/g, '<p style="page-break-after: always; break-after: page; margin: 0; padding: 0; height: 0; line-height: 0; font-size: 0; border: 0;">&nbsp;</p><p>1</p><p>2</p><p>3</p><p>4</p><p>5</p><p>6</p>')
        .replace(/<p>{LEERZEILE}<\/p>/g, '<p style="margin: 0; padding: 0; height: 1em; line-height: 1em; font-size: 1em;">&nbsp;</p>')
        .replace(/<p>{SEITENUMBRUCH}<\/p>/g, '<p class="page-break-after">&nbsp;</p>')
        .replace(/{UMBRUCH}/g, '<br/>')
        .replace(/<p>{UNTERSCHRIFTSFELD}<\/p>/, signatures)
        .replace(/<p>{UNTERSCHRIFTSFELD_KUNDE}<\/p>/, signatureCustomerTemplate)
        .replace(/{BANKVERBINDUNG}/g, '<p style="margin-left: 2em">Bankinstitut: Postbank<br/>IBAN: DE34 4767 0205 0063 0764 00<br/>BIC: DEUTDE3BP04</p>')
    //const text = values.text;
    //const textInTemplate = htmlTemplate.replace(/{CONTENT}/, text);

    const letterContentExtended = 
      `<p class="footerLeft">${location?.footerLeft?.replace(/\n/g, '<br/>')}</p>` +
      `<p class="footerCenter">${location?.footerCenter?.replace(/\n/g, '<br/>')}</p>` +
      `<p class="footerRight">${location?.footerRight?.replace(/\n/g, '<br/>')}</p>` +
      `<div id="senderAddress">
          ${location?.address?.replace(/\n/g, ' - ')}
					</div>` +
      `<div id="recipientAddress">
          ${address?.firstName}&nbsp;${address?.lastName}<br/>
          ${address?.street} &nbsp;${address?.houseNumber}<br/>
          ${address?.zipCode}&nbsp;${address?.city}
        </div>` +
        `<div id="date">${contractSetting.contractLocation}, den ${dayjs().format('DD.MM.YYYY')}</div>` +
        '<div id="conent">' + 
      (values.letterContent || fillContract(contractTemplate, lastContractDate, effectiveDate).filledLetter)
        .replace(/<p class="ql-align-((justify)|(center))"><br\/?><\/p>/g, '').replace(/<p class="ql-indent-1"><br\/?><\/p>/g, '').replace(/<p><br\/?><\/p>/g, '')
        .replace(/<h2.*?>/g, '<h2 data-break-after="avoid">')
        //.replace(/<p>{SEITENUMBRUCH}<\/p>/g, '<p style="page-break-after: always; break-after: page; margin: 0; padding: 0; height: 0; line-height: 0; font-size: 0; border: 0;">&nbsp;</p><p>1</p><p>2</p><p>3</p><p>4</p><p>5</p><p>6</p>')
        .replace(/<p>{LEERZEILE}<\/p>/g, '<p style="margin: 0; padding: 0; height: 1em; line-height: 1em; font-size: 1em;">&nbsp;</p>')
        .replace(/<p>{SEITENUMBRUCH}<\/p>/g, '<p class="page-break-after">&nbsp;</p>')
      + "</div>";

    let newContract = { ...values, contentExtended, letterContentExtended, contractTemplate: contractTemplateId && contractTemplateId !== -1 ? contractTemplateId : null, objectId: contractObjectId, objectType: contractObjectType }
    console.log("Submitting contract with values", values, "and newContract", newContract);
    if(contractId === 'new') {
      newContract = (await createContract(newContract) as any).data;
    } else {
      newContract.id = contractId;
      newContract = (await updateContract(newContract) as any).data;
    }
      
    console.log("New Contract", newContract);
    navigate(`${navigationBasePath}/${newContract.id}`, { replace: true });
  
    const response = await fetch(`/api/plamo/contract/${newContract.id}/unsigned-pdf`, { headers: { 'Authorization': `Bearer ${userToken}` } });
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a") as any;
    document.body.appendChild(a);
    a.style = "display: none;";
    a.href = url;
    a.download = `Vertrag ${signerFirstName} ${signerLastName}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);

    //const buffer = await blob.arrayBuffer();
    //await uploadContractUnsignedPdf({ id: newContract.id, data: buffer })
    
    setCreatingContract(false);
    showSuccessMessage();
  }, [contractId, signerFirstName, signerLastName, signerRole, contractTemplateId, setCreatingContract, createContract, updateContract, navigate, 
    contractSetting, userToken, contractObjectId, contractObjectType, navigationBasePath, location, fillContract, 
    address?.city, address?.firstName, address?.houseNumber, address?.lastName, address?.street, address?.zipCode, lastContractDate, effectiveDate, validUntil, contractTemplate]);

  return (<div style={{ height: '100%', overflow: "scroll" }}>
    <Form form={form} name="advanced_search" style={formStyle} onFinish={onFinish}>
    <h2>Vertrag { contractId === 'new' ? "erstellen" : "bearbeiten" }</h2>
    <Row>
      <FormItem spans={{ sm: 20, md: 20, lg: 20, xl: 16, xxl: 10 }} fieldName="contractTemplateId" label="Vorlage" type="enum" typeData={{ options: [{ value: -1, label: "Unbekannt"}, ...(data?.items?.map(t => ({ value: t.id, label: t.title })) || [])] }}/>
      <FormItem spans={{ sm: 20, md: 20, lg: 20, xl: 16, xxl: 10 }} fieldName="title" label="Titel" disabled={!!contractTemplate} />
    </Row>
    <Row>
      <FormItem fieldName="date" label="Vertragsdatum" type="date" />
      <FormItem fieldName="effectiveDate" label="Vertragsbeginn" type="date" />
      { showValidUntil && <FormItem fieldName="validUntil" label="Gültig bis" type="date" /> }
    </Row>
    { contractTemplate && <>
      <Row>
        <Col span={24}>  
          { missingFields?.map(missing => <Alert style={{ width: '100%', marginBottom: '1em' }} message={`Benötigtes Feld "${missing.name}" wurde nicht ausgefüllt ${missing.hint ? (' (' + missing.hint + ')') : ''}`} type="warning" />) }
        </Col>
      </Row>
    <Row>
      <Col span={24}>
        <Tabs defaultActiveKey='text' items={[
          {
            key: 'letterContent',
            label: 'Anschreiben',
            children: (
              <Form.Item
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}
                name={"letterContent"}
              >
                <Editor />
              </Form.Item>
            )
          },
          {
            key: 'content',
            label: 'Vertragstext',
            children: (
              <Form.Item name="content">
                <Editor />
              </Form.Item>
            )
          }
        ]} />
      </Col>
    </Row>
    </>
    }
    {
      (contractTemplate || contractTemplateId === -1) && <div style={{ textAlign: 'right' }}>
      <Space size="small">
        <Button loading={creatingContract} type="primary" htmlType="submit">
          Vertrag { contractId === 'new' ? "erstellen" : "aktualisieren" }
        </Button>
      </Space>
    </div>
    }
    </Form>
  </div>)
}

export default ContractDetails;